/**
 * Detecta el mismo paper en varias fuentes (o varias veces en una) y fusiona sus datos.
 *
 * Dos registros son el mismo paper si comparten DOI, PMID, PMCID o título normalizado
 * (solo títulos de 5+ palabras, para no confundir "Editorial" o "Introduction").
 * Se deduplica antes de clasificar, para no pagar Jev dos veces por el mismo paper.
 */

import { type Paper, normalizeDoi, paperUrl } from "./models";
import { RETRACTED_TYPE } from "./reliability";

const MIN_TITLE_WORDS = 5;
const TITLE_WORDS = 12;

/** Título en minúsculas, sin tildes ni puntuación; las primeras 12 palabras. */
export function titleKey(title: string): string | null {
  const words = title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  return words.length >= MIN_TITLE_WORDS ? words.slice(0, TITLE_WORDS).join(" ") : null;
}

export function paperKeys(p: Paper): string[] {
  const keys: string[] = [];
  const doi = normalizeDoi(p.doi);
  if (doi) keys.push(`doi:${doi}`);
  if (p.pmid) keys.push(`pmid:${p.pmid}`);
  if (p.pmcid) keys.push(`pmc:${p.pmcid.toUpperCase().replace(/^(?!PMC)/, "PMC")}`);
  const t = titleKey(p.title);
  if (t) keys.push(`title:${t}`);
  if (!keys.length) keys.push(`id:${p.source_id}`);
  return keys;
}

const isPreprint = (p: Paper) => p.publication_types.some((t) => /preprint|posted.?content/i.test(t));

/** Completa `target` con los datos de `other` (mismo paper visto en otra fuente). */
export function mergeInto(target: Paper, other: Paper): void {
  // Preprint y versión publicada: manda la publicada
  if (isPreprint(target) && !isPreprint(other)) {
    target.title = other.title;
    target.doi = other.doi ?? target.doi;
    target.journal = other.journal ?? target.journal;
    target.year = other.year ?? target.year;
    target.publication_types = [...other.publication_types, ...target.publication_types.filter((t) => t === RETRACTED_TYPE)];
  }
  // Basta con que una fuente lo marque como retractado
  if (other.publication_types.includes(RETRACTED_TYPE) && !target.publication_types.includes(RETRACTED_TYPE)) {
    target.publication_types = [...target.publication_types, RETRACTED_TYPE];
  }
  if ((other.abstract?.length ?? 0) > (target.abstract?.length ?? 0)) target.abstract = other.abstract;
  target.doi ??= normalizeDoi(other.doi);
  target.pmid ??= other.pmid;
  target.pmcid ??= other.pmcid;
  target.journal ??= other.journal;
  target.year ??= other.year;
  target.authors ??= other.authors;
  target.oa_url ??= other.oa_url;
  target.license ??= other.license;
  if (other.keywords?.length) target.keywords = [...new Set([...(target.keywords ?? []), ...other.keywords])];
  if (other.cited_by_count != null) {
    target.cited_by_count = Math.max(target.cited_by_count ?? 0, other.cited_by_count);
  }
  const sources = new Set([...(target.sources ?? []), ...(other.sources ?? (other.source ? [other.source] : []))]);
  target.sources = [...sources];
  target.url = paperUrl(target) ?? target.url ?? other.url;
}

export class Deduper {
  private readonly byKey = new Map<string, Paper>();

  /**
   * Registra un paper. Devuelve el registro canónico si es nuevo, o null si ya se había
   * visto (en ese caso sus datos se fusionan en el existente).
   */
  add(p: Paper): Paper | null {
    const keys = paperKeys(p);
    const existing = keys.map((k) => this.byKey.get(k)).find((x): x is Paper => !!x);
    if (existing) {
      mergeInto(existing, p);
      for (const k of paperKeys(existing)) if (!this.byKey.has(k)) this.byKey.set(k, existing);
      return null;
    }
    const canonical: Paper = { ...p, sources: p.sources ?? (p.source ? [p.source] : []) };
    for (const k of paperKeys(canonical)) this.byKey.set(k, canonical);
    return canonical;
  }

  has(p: Paper): boolean {
    return paperKeys(p).some((k) => this.byKey.has(k));
  }

  /** Registro canónico de un paper ya visto (o undefined). */
  find(p: Paper): Paper | undefined {
    return paperKeys(p)
      .map((k) => this.byKey.get(k))
      .find((x): x is Paper => !!x);
  }
}
